import { useEffect, useState, useMemo } from "react";
import { Calendar as CalendarIcon, Grid3x3, ArrowRight } from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import HexCard from "../components/HexCard";
import { api } from "../lib/api";
import { PORTALS_PLACEHOLDER } from "../lib/assets";
import { resolveUploadUrl } from "../components/ImageUploadField";

const parseDate = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};
const sameDay = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export default function PortalsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("grid"); // grid | calendar
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    api
      .get("/portals")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, []);

  const eventsByDate = useMemo(() => {
    const map = new Map();
    for (const p of items) {
      const d = parseDate(p.date);
      if (!d) continue;
      const key = d.toDateString();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    }
    return map;
  }, [items]);

  const datedEvents = useMemo(
    () =>
      items
        .filter((p) => parseDate(p.date))
        .sort((a, b) => parseDate(a.date) - parseDate(b.date)),
    [items]
  );

  const eventDates = useMemo(
    () => datedEvents.map((p) => parseDate(p.date)),
    [datedEvents]
  );

  const selectedEvents = selectedDate
    ? eventsByDate.get(selectedDate.toDateString()) || []
    : [];

  return (
    <div
      className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24"
      data-testid="portals-page"
    >
      <header className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12">
        <div className="lg:col-span-7 warm-up">
          <span className="label-accent">// Open channels</span>
          <h1 className="broadcast-headline text-5xl lg:text-7xl mt-4 text-[color:var(--nccc-cream)] chromatic">
            Portals
          </h1>
          <p className="mt-6 text-[color:var(--nccc-muted)] max-w-xl">
            Events, zines, broadcasts and video drops. Doorways out of the
            algorithm — into the room, into the page, into the analogue.
          </p>
        </div>
        <div className="lg:col-span-5 lg:col-start-8 flex justify-end warm-up-2">
          <div
            className="inline-flex border border-[color:var(--nccc-border)]"
            role="tablist"
            aria-label="View switch"
          >
            <button
              data-testid="portals-view-grid"
              onClick={() => setView("grid")}
              className={`flex items-center gap-2 px-4 py-2 label transition-colors ${
                view === "grid"
                  ? "bg-[color:var(--nccc-ochre)] text-[color:var(--nccc-bg)]"
                  : "hover:text-[color:var(--nccc-cream)]"
              }`}
            >
              <Grid3x3 size={14} /> Grid
            </button>
            <button
              data-testid="portals-view-calendar"
              onClick={() => setView("calendar")}
              className={`flex items-center gap-2 px-4 py-2 label transition-colors border-l border-[color:var(--nccc-border)] ${
                view === "calendar"
                  ? "bg-[color:var(--nccc-ochre)] text-[color:var(--nccc-bg)]"
                  : "hover:text-[color:var(--nccc-cream)]"
              }`}
            >
              <CalendarIcon size={14} /> Calendar
            </button>
          </div>
        </div>
      </header>

      {loading ? (
        <p className="label">// scanning frequencies...</p>
      ) : items.length === 0 ? (
        <p className="label">// no open channels.</p>
      ) : view === "grid" ? (
        <>
          <div className="honeycomb">
            {items.map((p) => (
              <HexCard
                key={p.id}
                testid={`hex-card-portal-${p.id}`}
                imageUrl={resolveUploadUrl(p.image_url) || PORTALS_PLACEHOLDER}
                eyebrow={p.kind?.toUpperCase()}
                title={p.name}
                subtitle={[p.date, p.location].filter(Boolean).join(" · ")}
                href={p.link_url || undefined}
              />
            ))}
          </div>
          <div className="honeycomb-spacer" />
        </>
      ) : (
        <CalendarView
          datedEvents={datedEvents}
          eventDates={eventDates}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          selectedEvents={selectedEvents}
        />
      )}
    </div>
  );
}

function CalendarView({
  datedEvents,
  eventDates,
  selectedDate,
  setSelectedDate,
  selectedEvents,
}) {
  const upcoming = datedEvents.filter((p) => parseDate(p.date) >= new Date(new Date().setHours(0,0,0,0)));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="portals-calendar">
      <div className="lg:col-span-5 border border-[color:var(--nccc-border)] p-4 bg-[color:var(--nccc-surface)]/40">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={setSelectedDate}
          modifiers={{ hasEvent: eventDates }}
          modifiersClassNames={{
            hasEvent:
              "relative after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:bg-[color:var(--nccc-ochre)] after:rounded-none",
          }}
          className="text-[color:var(--nccc-cream)]"
        />
        <p className="label mt-3">// dots mark transmissions</p>
      </div>

      <div className="lg:col-span-7">
        {selectedDate ? (
          <>
            <span className="label-accent">
              {selectedDate.toDateString()}
            </span>
            <h3 className="font-display text-2xl text-[color:var(--nccc-cream)] mt-2 mb-6">
              {selectedEvents.length > 0
                ? `${selectedEvents.length} transmission${selectedEvents.length > 1 ? "s" : ""}`
                : "No transmissions this day."}
            </h3>
            <div className="space-y-4">
              {selectedEvents.map((p) => (
                <EventRow key={p.id} item={p} />
              ))}
            </div>
          </>
        ) : (
          <>
            <span className="label-accent">// Upcoming</span>
            <h3 className="font-display text-2xl text-[color:var(--nccc-cream)] mt-2 mb-6">
              Upcoming transmissions
            </h3>
            {upcoming.length === 0 ? (
              <p className="label">// nothing scheduled.</p>
            ) : (
              <div className="space-y-4">
                {upcoming.map((p) => (
                  <EventRow key={p.id} item={p} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function EventRow({ item }) {
  const d = parseDate(item.date);
  return (
    <a
      href={item.link_url || "#"}
      target={item.link_url ? "_blank" : undefined}
      rel="noreferrer"
      className="block border border-[color:var(--nccc-border)] p-5 hover:border-[color:var(--nccc-ochre)] transition-colors bg-[color:var(--nccc-surface)]/40"
      data-testid={`calendar-event-${item.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <span className="label-accent">
            {item.kind?.toUpperCase()} · {d ? d.toDateString().slice(4) : item.date}
          </span>
          <h4 className="font-display text-xl text-[color:var(--nccc-cream)] mt-1">
            {item.name}
          </h4>
          {item.location && <p className="label mt-1">{item.location}</p>}
        </div>
        {item.link_url && (
          <span className="text-[color:var(--nccc-muted)]">
            <ArrowRight size={16} />
          </span>
        )}
      </div>
      {item.description && (
        <p className="mt-3 text-sm text-[color:var(--nccc-muted)] leading-relaxed line-clamp-2">
          {item.description}
        </p>
      )}
    </a>
  );
}
